import moment from 'moment/moment'

import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Image } from './Image'
import { Industry } from './Industry'
import { Payee } from './Payee'
import { FKType } from './SQLBuilder'
import { Sortable } from './Sortable'

type PersonConstructorArgs = Omit<ConstructorArgs<() => Person>, 'location'>

export enum PERSON_TYPE {
    PERSON_TYPE_UNKNOWN,
    PERSON_TYPE_CLIENT,
    PERSON_TYPE_PAYEE,
    PERSON_TYPE_USER,
    PERSON_TYPE_CONTACT,
    PERSON_TYPE_MANAGER,
    PERSON_TYPE_LOCATION,
    PERSON_TYPE_SUBSCRIBER,
    PERSON_TYPE_CLIENT_SUBACCOUNT,
}

export class Person extends BaseRecord implements Sortable {
    readonly recordType: string = 'person'

    readonly iSortable = true

    private _name: string | undefined
    label: string | undefined
    private _phone: string | undefined
    private _email: string | undefined
    line1: string | undefined
    line2: string | undefined
    city: string | undefined
    state: string | undefined
    postalCode: string | undefined
    countryCode: string | undefined
    address: string | undefined
    notes: string | undefined
    entity: string | undefined
    image: Image | undefined
    lastModifiedDate: moment.Moment | undefined
    subtypeType?: string

    constructor(data?: Partial<PersonConstructorArgs>, params?: { id?: string }) {
        super(data)
        if (params?.id) this.id = params.id
        if (!data) {
            return
        }

        if (data.subtype_type) {
            this.subtypeType = data.subtype_type
        }
        else if (data.subtype && typeof data.subtype !== 'string') {
            this.subtypeType = Person.modelToSubtypeType(data.subtype)
        }

        this._name = data.name ?? undefined
        this._phone = data.phone ?? undefined
        this.label = data.label ?? undefined
        this._email = data.email ?? undefined
        this.line1 = data.line1 ?? undefined
        this.line2 = data.line2 ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.entity = data.entity ?? undefined
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
    }

    clone(overrides?: Partial<PersonConstructorArgs>): Person {
        return new Person({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Person {
        const person = new Person(undefined, { id: id })
        return person
    }

    set name(name: string | undefined) {
        this._name = name
    }

    set phone(phone: string | undefined) {
        this._phone = phone
    }

    set email(email: string | undefined) {
        this._email = email
    }

    get name(): string | undefined {
        return this._name
    }

    get phone(): string | undefined {
        return this._phone
    }

    get email(): string | undefined {
        return this._email
    }

    get firstName(): string | undefined {
        return Person.firstName(this.name)
    }

    static firstName(name: string | undefined): string | undefined {
        if (name) {
            name = name.trim()
            if (name.lastIndexOf(' ') !== -1) return name.substring(0, name.lastIndexOf(' ')).trim()
        }
        return name
    }

    get lastName(): string | undefined {
        return Person.lastName(this.name)
    }

    static lastName(name: string | undefined): string | undefined {
        if (name) {
            name = name.trim()
            if (name.lastIndexOf(' ') !== -1) return name.substring(name.lastIndexOf(' ')).trim()
        }
        return undefined
    }

    get e164(): string | undefined {
        let phone = this.phone
        if (phone) {
            phone = phone.replace(/\D+/g, '')
            if (phone.startsWith('1')) phone = phone.substring(1)
            return `+1${phone}`
        }
        return undefined
    }

    get initials(): string {
        const firstName = this.firstName
        const lastName = this.lastName
        let initials = ''
        if (!!lastName) initials = `${firstName?.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
        else if (firstName) initials = firstName.charAt(0).toUpperCase()
        for (const c of initials) {
            if (c >= 'A' && c <= 'z') return initials
        }
        return '#'
    }

    get initialsTextColor(): number {
        if (this.id === undefined) {
            return 0
        }
        return Person.initialsTextColor(this.id)
    }

    static initialsTextColor(id: string): number {
        const colors = [0xfa3c69, 0xe57c45, 0x412fb6, 0x60ab6b, 0x3e9de9, 0xc8a022]
        let hash = 0
        for (let i = 0; i < id.length; i++) hash = (hash << 5) - hash + id.charCodeAt(i)
        return colors[Math.abs(hash) % colors.length]
    }

    get initialsBackgroundColor(): number {
        if (this.id === undefined) {
            return 0
        }
        return Person.initialsBackgroundColor(this.id)
    }

    static initialsBackgroundColor(id: string): number {
        const colors = [0xfdeef1, 0xfff1e9, 0xefecff, 0xecf7ee, 0xe9f5ff, 0xfdf9ec]
        let hash = 0
        for (let i = 0; i < id.length; i++) hash = (hash << 5) - hash + id.charCodeAt(i)
        return colors[Math.abs(hash) % colors.length]
    }

    get sortKey(): string | undefined {
        return this.sortName
    }

    get sortName(): string | undefined {
        return Person.sortName(this.name)
    }

    static sortName(name: string | undefined): string | undefined {
        if (name) name = name.trim()
        let sortedName = name
        if (sortedName === undefined) sortedName = ''
        if (sortedName.indexOf(' ') === -1) return sortedName
        const firstName = Person.firstName(name)
        const lastName = Person.lastName(name)
        if (lastName)
            /* TODO: read and respect default sort name settings on Android */
            sortedName = `${lastName} ${firstName}`
        else sortedName = firstName
        return sortedName
    }

    //todo: implement SpannableStringBuilder, TypefaceSpan
    // get displayName(): string {
    //     let displayName = this.name
    //     const lastName = this.lastName
    //     const displayString = new SpannableStringBuilder(displayName)
    //     if (displayName === undefined)
    //         displayName = ""
    //     if (lastName)
    //         displayString.setSpan(new TypefaceSpan(DEFAULT_SEMIBOLD_FONT), displayName.indexOf(lastName), displayName.indexOf(lastName) + lastName.length, Spanned.SPAN_INCLUSIVE_INCLUSIVE)
    //     else
    //         displayString.setSpan(new TypefaceSpan(DEFAULT_SEMIBOLD_FONT), 0, displayName.length, Spanned.SPAN_INCLUSIVE_INCLUSIVE)
    //     return displayString.toString()
    // }

    get location(): string | undefined {
        if (this.address) return this.address
        return Person.locationString(
            this.line1,
            this.line2,
            this.city,
            this.state,
            this.postalCode,
            undefined
        )
    }

    static locationString(
        line1: string | undefined,
        line2: string | undefined,
        city: string | undefined,
        state: string | undefined,
        zip: string | undefined,
        location: string | undefined
    ): string {
        const blocks: string[] = []
        if (line1) blocks.push(line1)
        if (line2) blocks.push(line2)
        const streetAddress = blocks.join(', ')
        blocks.length = 0
        if (city) blocks.push(city)
        if (state) blocks.push(state)
        const cityAndState = blocks.join(', ')
        blocks.length = 0
        if (streetAddress) {
            blocks.push(streetAddress)
            if (cityAndState && zip) blocks.push(`${cityAndState} ${zip}`)
            else if (cityAndState) blocks.push(cityAndState)
        }
        else if (cityAndState && zip) blocks.push(`${cityAndState} ${zip}`)
        else if (cityAndState) blocks.push(cityAndState)
        return blocks.length > 0 ? blocks.join(', ') : location || ''
    }

    get cityState(): string {
        const blocks: string[] = []
        if (this.city) blocks.push(this.city)
        if (this.state) blocks.push(this.state)
        return blocks.join(', ')
    }

    static subtypeTypeToModel(subtype_type: string) {
        switch (subtype_type) {
            case 'payee':
                return Payee
            default:
                return undefined
        }
    }

    static modelToSubtypeType<ModelT extends BaseRecord>(model: ModelT): string | undefined {
        if (model instanceof Payee) return 'payee'
        return undefined
    }

    get subtype(): BaseRecord | undefined {
        if (this.subtypeType === undefined) {
            return undefined
        }
        const model = Person.subtypeTypeToModel(this.subtypeType)
        if (model === undefined) {
            return undefined
        }
        return this.getForeignRecord('subtype', model as any)
    }
    set subtype(subtype: BaseRecord | undefined) {
        if (subtype !== undefined) {
            this.subtypeType = Person.modelToSubtypeType(subtype)
        }
        this.setForeignRecord('subtype', subtype)
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.industry) this.industry = BaseRecord.fromSerializer(data.industry, Industry)
        if (data.subtype && data.subtype_type) {
            const modelType = Person.subtypeTypeToModel(data.subtype_type)
            if (modelType) {
                this.subtype = BaseRecord.fromSerializer(data.subtype, modelType)
            }
        }
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        images: ['image'],
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['name'] = this.name
        dict['image'] = this.image?.id ?? ''
        dict['phone'] = this.phone ?? ''
        if (this.label) dict['label'] = this.label
        dict['email'] = this.email ?? ''
        dict['line1'] = this.line1 ?? ''
        dict['line2'] = this.line2 ?? ''
        dict['city'] = this.city ?? ''
        dict['postal_code'] = this.postalCode ?? ''
        dict['entity'] = this.entity ?? ''
        dict['address'] = this.address ?? ''
        return dict
    }

    get industry(): Industry | undefined {
        return this.getForeignRecord('industry', Industry)
    }
    set industry(industry: Industry | undefined) {
        this.setForeignRecord('industry', industry)
    }
}
